import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../App";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { TrendingUp, TrendingDown, Minus, Shield, Award, BookOpen } from "lucide-react";

/**
 * Skill Rating history chart — read-only view for the student profile.
 * Shows Elo evolution over time with per-point markers (exam vs evaluation).
 */
export default function SkillRatingHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoverIdx, setHoverIdx] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/me/skill-rating-history`, { withCredentials: true });
      setData(resp.data);
    } catch {
      setData({ entries: [], current_rating: 1000, total: 0, by_source: { exam: 0, training: 0 } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  if (loading) {
    return (
      <Card className="instrument-panel">
        <CardContent className="py-6 text-center text-slate-500 text-xs">A carregar histórico…</CardContent>
      </Card>
    );
  }

  const entries = data?.entries || [];
  const current = data?.current_rating ?? 1000;
  const deltas = data?.deltas || { d7: null, d30: null };

  // Helper to render a short-term delta pill (7d / 30d)
  const DeltaPill = ({ label, value }) => {
    if (value === null || value === undefined) {
      return (
        <span
          className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800/60 text-slate-500 border border-slate-700/50"
          data-testid={`skill-rating-delta-${label}`}
          title="Sem dados suficientes para calcular a variação"
        >
          {label}: —
        </span>
      );
    }
    const color =
      value > 0
        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
        : value < 0
          ? "bg-red-500/10 text-red-400 border-red-500/30"
          : "bg-slate-800/60 text-slate-400 border-slate-700/50";
    const sign = value > 0 ? "+" : "";
    return (
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full border font-mono ${color}`}
        data-testid={`skill-rating-delta-${label}`}
        title={`Variação nos últimos ${label === "7d" ? "7" : "30"} dias`}
      >
        {label}: {sign}
        {value}
      </span>
    );
  };

  // Empty state
  if (entries.length === 0) {
    return (
      <Card className="instrument-panel" data-testid="skill-rating-history-card">
        <CardHeader>
          <CardTitle className="font-chivo text-cockpit-text-bright flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Skill Rating
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="text-3xl font-bold text-cyan-300">{current}</div>
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              O teu rating ainda não tem movimento registado.
              <br />
              Faz um exame ou uma sessão em <strong className="text-purple-300">Modo Avaliação</strong> para começar.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Build chart geometry — wider, shorter to fit a full-width dashboard row
  const W = 900;
  const H = 120;
  const PAD = { top: 10, right: 16, bottom: 22, left: 40 };
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;

  const minRating = Math.min(...entries.map((e) => Math.min(e.old_rating, e.new_rating)));
  const maxRating = Math.max(...entries.map((e) => Math.max(e.old_rating, e.new_rating)));
  const pad = Math.max(20, (maxRating - minRating) * 0.1);
  const yMin = Math.max(0, minRating - pad);
  const yMax = maxRating + pad;
  const range = yMax - yMin || 1;

  const step = entries.length > 1 ? innerW / (entries.length - 1) : 0;
  const points = entries.map((e, i) => {
    const x = PAD.left + i * step;
    const y = PAD.top + innerH - ((e.new_rating - yMin) / range) * innerH;
    return { x, y, ...e };
  });
  const polyline = points.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");

  const firstRating = entries[0].old_rating;
  const totalDelta = current - firstRating;
  const TrendIcon = totalDelta > 0 ? TrendingUp : totalDelta < 0 ? TrendingDown : Minus;
  const trendColor =
    totalDelta > 0 ? "text-emerald-400" : totalDelta < 0 ? "text-red-400" : "text-slate-400";

  const hovered = hoverIdx !== null ? points[hoverIdx] : null;

  return (
    <Card className="instrument-panel" data-testid="skill-rating-history-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <CardTitle className="font-chivo text-lg flex items-center gap-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            Skill Rating
          </CardTitle>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-bold text-cyan-300" data-testid="current-rating">{current}</span>
              <span className={`text-xs font-semibold flex items-center gap-1 ${trendColor}`}>
                <TrendIcon className="w-3 h-3" />
                {totalDelta > 0 ? "+" : ""}
                {totalDelta}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <DeltaPill label="7d" value={deltas.d7} />
              <DeltaPill label="30d" value={deltas.d30} />
            </div>
            <div className="flex items-center gap-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1">
                <Award className="w-3 h-3 text-amber-400" />
                {data.by_source.exam} exame{data.by_source.exam === 1 ? "" : "s"}
              </span>
              <span className="inline-flex items-center gap-1">
                <BookOpen className="w-3 h-3 text-purple-400" />
                {data.by_source.training} avaliaç{data.by_source.training === 1 ? "ão" : "ões"}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-24 sm:h-28"
          preserveAspectRatio="none"
          onMouseLeave={() => setHoverIdx(null)}
          data-testid="skill-rating-chart"
        >
          {/* Y-axis guides */}
          {[0, 0.5, 1].map((t) => {
            const y = PAD.top + innerH * t;
            const val = Math.round(yMax - (yMax - yMin) * t);
            return (
              <g key={`grid-${t}`}>
                <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} stroke="#1e293b" strokeWidth="0.5" strokeDasharray="2 3" />
                <text x={PAD.left - 6} y={y + 3} textAnchor="end" fontSize="9" fill="#64748b">{val}</text>
              </g>
            );
          })}
          {/* Starting rating horizontal reference */}
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={PAD.top + innerH - ((firstRating - yMin) / range) * innerH}
            y2={PAD.top + innerH - ((firstRating - yMin) / range) * innerH}
            stroke="#475569"
            strokeWidth="0.5"
            strokeDasharray="4 4"
          />
          {/* Main polyline */}
          <polyline
            points={polyline}
            fill="none"
            stroke="#22d3ee"
            strokeWidth="1.5"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
          {/* Points — colour encodes source */}
          {points.map((p, i) => {
            const fill = p.source === "exam" ? "#fbbf24" : "#c084fc";
            return (
              <g
                key={p.timestamp || `pt-${i}`}
                onMouseEnter={() => setHoverIdx(i)}
                style={{ cursor: "pointer" }}
              >
                <circle cx={p.x} cy={p.y} r="3.5" fill={fill} stroke="#0f172a" strokeWidth="1" />
                <circle cx={p.x} cy={p.y} r="10" fill="transparent" />
              </g>
            );
          })}
        </svg>

        {/* Hover detail */}
        {hovered ? (
          <div className="mt-2 p-2 bg-slate-900/60 border border-slate-700 rounded text-xs" data-testid="hover-detail">
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-300">
                {hovered.source === "exam" ? (
                  <span className="inline-flex items-center gap-1 text-amber-300">
                    <Award className="w-3 h-3" /> Exame
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-purple-300">
                    <BookOpen className="w-3 h-3" /> Avaliação
                  </span>
                )}
              </span>
              <span className="text-slate-500 text-[10px]">
                {new Date(hovered.timestamp).toLocaleString("pt-PT", {
                  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-slate-400">
                {hovered.old_rating} → <strong className="text-cyan-300">{hovered.new_rating}</strong>
              </span>
              <span className={hovered.delta >= 0 ? "text-emerald-400 font-semibold" : "text-red-400 font-semibold"}>
                {hovered.delta >= 0 ? "+" : ""}
                {hovered.delta}
              </span>
              <span className="text-slate-500 text-[10px]">
                · precisão {Math.round(hovered.accuracy * 100)}%
              </span>
            </div>
          </div>
        ) : (
          <p className="text-[10px] text-slate-600 mt-2 text-center">
            Passa o rato sobre um ponto para veres o detalhe.{" "}
            <span className="text-amber-400">● exames</span>{" · "}
            <span className="text-purple-400">● sessões de avaliação</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
